import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { ClipboardCheck } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { ROLES } from "@/lib/constants/options";
import { useAuthStore } from "@/features/auth";
import {
  CustomSurveysTab,
  ResponsesTab,
  StandardSurveysTab,
  SurveyTakeDialog,
} from "../components";
import { useSurveysUiStore } from "../hooks";

const SurveysPage = () => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const surveysUiStore = useSurveysUiStore();

  const showCustomTab = ![ROLES.SYSTEM_ADMIN, ROLES.BUSINESS_UNIT].includes(
    user?.role,
  );

  const tabs = useMemo(() => {
    const base = [
      { key: "standard", label: t("Standard Surveys") },
      { key: "custom", label: t("Custom Surveys"), hidden: !showCustomTab },
      { key: "responses", label: t("Responses") },
    ];
    return base.filter((tab) => !tab.hidden);
  }, [showCustomTab, t]);

  useEffect(() => {
    if (!tabs.find((tab) => tab.key === surveysUiStore.activeTab)) {
      const fallback = tabs[0]?.key || "standard";
      if (fallback !== surveysUiStore.activeTab) {
        surveysUiStore.setActiveTab(fallback);
      }
    }
  }, [surveysUiStore, tabs]);

  return (
    <div className="flex h-full flex-col space-y-5">
      <header className="overflow-hidden rounded-radius border border-border bg-gradient-to-tl from-secondary to-primary dark:from-primary/40 dark:to-card px-6 py-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-white">
              <ClipboardCheck className="h-5 w-5" />
              <h1 className="text-2xl font-semibold">{t("Surveys")}</h1>
            </div>
            <p className="mt-2 text-sm text-white/70">
              {t(
                "Take standard surveys or create custom question sets for your organization.",
              )}
            </p>
          </div>
        </div>
      </header>

      <main className="flex-1 space-y-5">
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1 text-sm font-semibold text-muted-foreground">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={cn(
                "rounded-full px-4 py-2 transition",
                surveysUiStore.activeTab === tab.key
                  ? "bg-secondary text-secondary-foreground shadow-sm"
                  : "hover:text-foreground",
              )}
              onClick={() => surveysUiStore.setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="rounded-radius border border-border bg-card/80 p-4 shadow-sm">
          {surveysUiStore.activeTab === "standard" ? (
            <StandardSurveysTab />
          ) : null}
          {surveysUiStore.activeTab === "custom" && showCustomTab ? (
            <CustomSurveysTab />
          ) : null}
          {surveysUiStore.activeTab === "responses" ? <ResponsesTab /> : null}
        </div>
      </main>

      <SurveyTakeDialog />
    </div>
  );
};

export default observer(SurveysPage);
